import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { PageHeader, Panel, Loading, StatusBadge } from "@/components/common";
import { PaperPlaneTilt, Clock, FilmStrip, Warning, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const PLATFORM_LABELS = {
  youtube: "YouTube Shorts",
  instagram: "Instagram Reels",
  facebook: "Facebook Reels",
  tiktok: "TikTok",
  x: "X",
};

export default function Publisher() {
  const { active } = useStudio();
  const [assets, setAssets] = useState(null);
  const [hub, setHub] = useState(null);
  const [assetId, setAssetId] = useState("");
  const [platforms, setPlatforms] = useState(["youtube", "instagram", "facebook", "tiktok"]);
  const [title, setTitle] = useState("The Wizard's Impossible Delivery");
  const [caption, setCaption] = useState("One enchanted cart. One impossible delivery. What could go wrong?");
  const [scheduledFor, setScheduledFor] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/assets"), api.get("/social-hub/platforms")])
      .then(([assetRes, hubRes]) => {
        const media = (assetRes.data.assets || []).filter((item) =>
          (item.mime || "").startsWith("video/") || (item.mime || "").startsWith("image/")
        );
        setAssets(media);
        setHub(hubRes.data);
        const firstVideo = media.find((item) => (item.mime || "").startsWith("video/"));
        setAssetId((firstVideo || media[0] || {}).id || "");
      });
  }, [active?.id]);

  const selected = useMemo(() => (assets || []).find((item) => item.id === assetId), [assets, assetId]);

  const togglePlatform = (id) => setPlatforms((current) =>
    current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
  );

  const queue = async () => {
    if (!assetId) return toast.error("Choose a real clip or image first");
    if (!platforms.length) return toast.error("Choose at least one platform");
    setBusy(true);
    try {
      const { data } = await api.post("/social-hub/queue", {
        asset_id: assetId,
        platforms,
        title,
        caption,
        scheduled_for: scheduledFor || null,
        language: "Visual-first",
      });
      if (data.status === "AWAITING_CONNECTION") {
        toast.warning(`Saved safely. Connect ${data.missing_connections.join(", ")} before publishing.`);
      } else {
        toast.success(scheduledFor ? "Scheduled in the publishing queue" : "Ready in the publishing queue");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not prepare this post");
    } finally {
      setBusy(false);
    }
  };

  if (!assets || !hub) return <Loading />;

  return (
    <div>
      <PageHeader
        icon={PaperPlaneTilt}
        title="PinPilot Publisher"
        subtitle="Choose one approved master clip, tailor its message and send it to every connected channel from one queue."
        badge={<StatusBadge status={hub.connected ? "LIVE" : "SETUP REQUIRED"} />}
      />

      <div className="grid gap-6 xl:grid-cols-[1fr_1.15fr]">
        <Panel>
          <div className="mb-4 flex items-center gap-2">
            <FilmStrip className="text-[#B88746]" weight="fill" />
            <h3 className="font-display font-bold text-white">Approved media</h3>
          </div>

          {assets.length === 0 ? (
            <div className="rounded-xl border border-[#FFB800]/25 bg-[#FFB800]/8 p-4 text-sm text-zinc-300">
              Produce or upload a real PinPilot clip first. Only real stored media can be selected.
            </div>
          ) : (
            <>
              <select
                value={assetId}
                onChange={(event) => setAssetId(event.target.value)}
                className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none"
              >
                {assets.map((asset) => (
                  <option key={asset.id} value={asset.id}>
                    {asset.title || asset.filename || asset.kind || "Media"} · {asset.id}
                  </option>
                ))}
              </select>

              <div className="mt-4 overflow-hidden rounded-2xl border border-white/10 bg-black/40">
                {(selected?.mime || "").startsWith("video/") ? (
                  <video src={selected.url} controls playsInline className="aspect-[9/16] max-h-[560px] w-full object-contain" />
                ) : (
                  <img src={selected?.url} alt="Selected media" className="aspect-[9/16] max-h-[560px] w-full object-contain" />
                )}
              </div>
            </>
          )}
        </Panel>

        <Panel>
          <div className="space-y-4">
            <Field label="Post title">
              <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={160}
                className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none focus:border-[#6FA9A5]" />
            </Field>
            <Field label="Caption">
              <textarea value={caption} onChange={(event) => setCaption(event.target.value)} rows={4} maxLength={5000}
                className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none focus:border-[#6FA9A5]" />
            </Field>
            <Field label="Publish time (optional)">
              <input type="datetime-local" value={scheduledFor} onChange={(event) => setScheduledFor(event.target.value)}
                className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-white outline-none [color-scheme:dark]" />
            </Field>

            <div>
              <div className="mb-2 text-xs uppercase tracking-wider text-zinc-500">Destinations</div>
              <div className="grid gap-2 sm:grid-cols-2">
                {hub.platforms.map((platform) => {
                  const selectedPlatform = platforms.includes(platform.id);
                  return (
                    <button key={platform.id} type="button" onClick={() => togglePlatform(platform.id)}
                      className={`flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${
                        selectedPlatform ? "border-[#6FA9A5]/60 bg-[#276A6B]/15" : "border-white/8 bg-black/20"
                      }`}>
                      {selectedPlatform ? <CheckCircle weight="fill" className="text-[#8CC5C0]" /> : <span className="h-4 w-4 rounded-full border border-white/20" />}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-white">{PLATFORM_LABELS[platform.id]}</div>
                        <div className="text-[10px] text-zinc-500">{platform.status}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {hub.connected < hub.total && (
              <div className="flex items-start gap-2 rounded-xl border border-[#FFB800]/25 bg-[#FFB800]/8 p-3">
                <Warning className="mt-0.5 text-[#FFB800]" weight="fill" />
                <p className="text-xs leading-relaxed text-zinc-300">
                  Unconnected destinations will remain safely in AWAITING CONNECTION. Nothing is falsely marked published.
                </p>
              </div>
            )}

            <button onClick={queue} disabled={busy || !assetId || !title.trim()}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#276A6B] to-[#B88746] px-5 py-3 font-semibold text-white disabled:opacity-50">
              {scheduledFor ? <Clock weight="fill" /> : <PaperPlaneTilt weight="fill" />}
              {busy ? "Preparing…" : scheduledFor ? "Add to schedule" : "Add to publishing queue"}
            </button>
          </div>
        </Panel>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs uppercase tracking-wider text-zinc-500">{label}</span>
      {children}
    </label>
  );
}
